import { useAuthStore } from '@/stores/authStore';
import {
    Box,
    Button,
    Group,
    SegmentedControl,
    Stack,
    Text,
} from '@mantine/core';
import { IconCircleCheck } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useMarkAllAsRead,
    useNotificationInfiniteQuery,
} from '../services/useNotificationQueries';
import NotificationItem from './NotificationItem';
import { useNotificationStore } from '@/stores/notificationStore';

export default function NotificationList({ onClose }: { onClose: () => void }) {
    const { user } = useAuthStore();
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
        useNotificationInfiniteQuery(user?.id || '');
    const markAllAsRead = useMarkAllAsRead();
    const [filter, setFilter] = useState('all');
    const { t } = useTranslation(['common', 'notification']);
    const { unreadCount } = useNotificationStore();

    // 무한 스크롤 구현
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        // 컨텐츠 내 패딩을 고려하여 거의 바닥에 도달했을 때 추가 로딩
        const nearBottom =
            e.currentTarget.scrollHeight -
                e.currentTarget.scrollTop -
                e.currentTarget.clientHeight <
            20;

        if (nearBottom && hasNextPage && !isFetchingNextPage) {
            void fetchNextPage();
        }
    };

    return (
        <Box>
            <Text p="24 16 12 24" fw={400} fz="lg">
                {t('notification:title')}
            </Text>
            <Group justify="space-between" p="0 16 8 24">
                <SegmentedControl
                    size="xs"
                    data={[
                        { value: 'all', label: t('notification:all') },
                        {
                            value: 'notRead',
                            label: t('notification:unread_count', {
                                count: unreadCount,
                            }),
                        },
                    ]}
                    value={filter}
                    onChange={setFilter}
                />
                <Button
                    variant="subtle"
                    size="xs"
                    color="gray"
                    onClick={() =>
                        void markAllAsRead.mutate({ userId: user?.id || '' })
                    }
                    leftSection={<IconCircleCheck size={16} />}
                    styles={{
                        section: {
                            padding: 0,
                            margin: '0 4px 0 0',
                        },
                    }}
                >
                    {t('notification:markAsRead')}
                </Button>
            </Group>

            <div
                style={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    scrollbarWidth: 'thin',
                }}
                onScroll={handleScroll}
            >
                {isLoading ? (
                    <Text fz="xs" ta="center" py="md" c="dimmed">
                        {t('common:notification.loading')}
                    </Text>
                ) : data?.pages.flat().length ? (
                    <Stack gap={0}>
                        {data?.pages
                            .flat()
                            .filter(
                                notification =>
                                    filter === 'all' ||
                                    (filter === 'notRead' &&
                                        !notification.is_read)
                            )
                            .map(notification => (
                                <NotificationItem
                                    key={notification.id}
                                    notification={notification}
                                    onClose={onClose}
                                />
                            ))}

                        {isFetchingNextPage && (
                            <Text fz="xs" ta="center" py="xs" c="dimmed">
                                {t('common:notification.message', {
                                    ns: 'common',
                                })}
                            </Text>
                        )}
                    </Stack>
                ) : (
                    <Text fz="xs" ta="center" py="md" c="dimmed">
                        {t('notification:emptyState')}
                    </Text>
                )}
            </div>
        </Box>
    );
}
